/**
 * Platform & Marketplace Service — Workflow Manager
 *
 * CRUD + business logic for the extensible platform layer.
 * Maps to database_platform_marketplace.sql tables.
 */

import { supabase } from '../lib/supabaseClient';
import type {
  MarketplaceApp,
  WorkspaceApp,
  AppPermission,
  WorkspaceAppPermission,
  AppSubscription,
  EventBusEntry,
  AppWebhook,
  Automation,
  AutomationRun,
  AppLog,
  AppUsageMetric,
  AppReview,
  AppVersion,
  FeaturedApp,
  DashboardWidget,
  DeveloperAccount,
  MarketplaceStats,
  AppCategory,
} from '../types/platformTypes';

// ─── Marketplace ──────────────────────────────────────────────

export async function getMarketplaceApps(filters?: {
  category?: AppCategory;
  search?: string;
  status?: string;
}): Promise<MarketplaceApp[]> {
  let query = supabase
    .from('apps')
    .select('*')
    .eq('status', 'published')
    .order('downloads', { ascending: false });

  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
  }

  const { data, error } = await query.limit(100);
  if (error) {
    console.error('[platformService] getMarketplaceApps error:', error.message);
    return [];
  }
  return (data as MarketplaceApp[]) || [];
}

export async function getAppBySlug(slug: string): Promise<MarketplaceApp | null> {
  const { data, error } = await supabase
    .from('apps')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) return null;
  return data as MarketplaceApp;
}

export async function getAppById(id: string): Promise<MarketplaceApp | null> {
  const { data, error } = await supabase
    .from('apps')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return null;
  return data as MarketplaceApp;
}

export async function getFeaturedApps(): Promise<FeaturedApp[]> {
  const { data, error } = await supabase
    .from('featured_apps')
    .select('*, app:apps(*)')
    .order('position', { ascending: true })
    .limit(10);

  if (error) return [];
  return (data as FeaturedApp[]) || [];
}

export async function getMarketplaceStats(): Promise<MarketplaceStats> {
  const { data, error } = await supabase
    .from('marketplace_stats')
    .select('*')
    .single();

  if (error) {
    return {
      total_apps: 0,
      pending_review: 0,
      workspaces_with_apps: 0,
      total_downloads: 0,
      active_developers: 0,
      active_automations: 0,
      events_24h: 0,
    };
  }
  return data as MarketplaceStats;
}

// ─── Workspace Apps (install / manage) ────────────────────────

export async function getWorkspaceApps(workspaceId: string): Promise<WorkspaceApp[]> {
  const { data, error } = await supabase
    .from('workspace_apps')
    .select('*, app:apps(*)')
    .eq('workspace_id', workspaceId)
    .neq('status', 'removed')
    .order('installed_at', { ascending: false });

  if (error) return [];
  return (data as WorkspaceApp[]) || [];
}

export async function installApp(workspaceId: string, appId: string): Promise<WorkspaceApp | null> {
  // Increment download counter
  await supabase.rpc('increment_app_download', { p_app_id: appId });

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('workspace_apps')
    .insert({
      workspace_id: workspaceId,
      app_id: appId,
      installed_by: user.id,
      status: 'installed',
      enabled: true,
    })
    .select()
    .single();

  if (error) {
    console.error('[platformService] installApp error:', error.message);
    return null;
  }

  // Emit event
  await emitEvent(workspaceId, 'app.installed', { app_id: appId });

  return data as WorkspaceApp;
}

export async function toggleApp(workspaceAppId: string, enabled: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('workspace_apps')
    .update({
      enabled,
      status: enabled ? 'enabled' : 'disabled',
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceAppId);

  return !error;
}

export async function uninstallApp(workspaceAppId: string): Promise<boolean> {
  const { error } = await supabase
    .from('workspace_apps')
    .update({
      status: 'removed',
      enabled: false,
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceAppId);

  return !error;
}

export async function updateAppConfig(
  workspaceAppId: string,
  config: Record<string, unknown>
): Promise<boolean> {
  const { error } = await supabase
    .from('workspace_apps')
    .update({ config, updated_at: new Date().toISOString() })
    .eq('id', workspaceAppId);

  return !error;
}

// ─── App Permissions ──────────────────────────────────────────

export async function getAppPermissions(appId: string): Promise<AppPermission[]> {
  const { data, error } = await supabase
    .from('app_permissions')
    .select('*')
    .eq('app_id', appId);

  if (error) return [];
  return (data as AppPermission[]) || [];
}

export async function getWorkspaceAppPermissions(
  workspaceId: string,
  appId: string
): Promise<WorkspaceAppPermission[]> {
  const { data, error } = await supabase
    .from('workspace_app_permissions')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('app_id', appId);

  if (error) return [];
  return (data as WorkspaceAppPermission[]) || [];
}

export async function grantAppPermission(
  workspaceId: string,
  appId: string,
  permissionKey: string,
  granted: boolean
): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase
    .from('workspace_app_permissions')
    .upsert(
      {
        workspace_id: workspaceId,
        app_id: appId,
        permission_key: permissionKey,
        granted,
        granted_by: user.id,
        granted_at: new Date().toISOString(),
      },
      { onConflict: 'workspace_id,app_id,permission_key' }
    );

  return !error;
}

// ─── App Subscriptions ────────────────────────────────────────

export async function getAppSubscription(
  workspaceId: string,
  appId: string
): Promise<AppSubscription | null> {
  const { data, error } = await supabase
    .from('app_subscriptions')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('app_id', appId)
    .single();

  if (error) return null;
  return data as AppSubscription;
}

export async function getWorkspaceAppSubscriptions(
  workspaceId: string
): Promise<AppSubscription[]> {
  const { data, error } = await supabase
    .from('app_subscriptions')
    .select('*')
    .eq('workspace_id', workspaceId)
    .neq('status', 'cancelled');

  if (error) return [];
  return (data as AppSubscription[]) || [];
}

// ─── Event Bus ────────────────────────────────────────────────

export async function emitEvent(
  workspaceId: string,
  eventType: string,
  payload: Record<string, unknown> = {},
  source = 'system'
): Promise<string | null> {
  const { data, error } = await supabase
    .rpc('emit_event', {
      p_workspace_id: workspaceId,
      p_event_type: eventType,
      p_payload: payload,
      p_source: source,
    });

  if (error) {
    console.error('[platformService] emitEvent error:', error.message);
    return null;
  }
  return data as string;
}

export async function getEventBusEntries(
  workspaceId?: string,
  limit = 50
): Promise<EventBusEntry[]> {
  let query = supabase
    .from('event_bus')
    .select('*');

  if (workspaceId) {
    query = query.eq('workspace_id', workspaceId);
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) return [];
  return (data as EventBusEntry[]) || [];
}

// ─── App Webhooks ─────────────────────────────────────────────

export async function getAppWebhooks(
  workspaceId: string,
  appId?: string
): Promise<AppWebhook[]> {
  let query = supabase
    .from('app_webhooks')
    .select('*')
    .eq('workspace_id', workspaceId);

  if (appId) {
    query = query.eq('app_id', appId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) return [];
  return (data as AppWebhook[]) || [];
}

export async function createAppWebhook(webhook: {
  app_id: string;
  workspace_id: string;
  event_type: string;
  endpoint_url: string;
  secret: string;
}): Promise<AppWebhook | null> {
  const { data, error } = await supabase
    .from('app_webhooks')
    .insert(webhook)
    .select()
    .single();

  if (error) return null;
  return data as AppWebhook;
}

export async function toggleAppWebhook(webhookId: string, enabled: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('app_webhooks')
    .update({ enabled })
    .eq('id', webhookId);

  return !error;
}

// ─── Automations ──────────────────────────────────────────────

export async function getAutomations(workspaceId?: string): Promise<Automation[]> {
  let query = supabase
    .from('automations')
    .select('*');

  if (workspaceId) {
    query = query.eq('workspace_id', workspaceId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) return [];
  return (data as Automation[]) || [];
}

export async function getAutomation(id: string): Promise<Automation | null> {
  const { data, error } = await supabase
    .from('automations')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return null;
  return data as Automation;
}

export async function createAutomation(automation: {
  workspace_id: string;
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config?: Record<string, unknown>;
  action_type: string;
  action_config?: Record<string, unknown>;
  conditions?: Record<string, unknown>[];
}): Promise<Automation | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('automations')
    .insert({ ...automation, created_by: user.id })
    .select()
    .single();

  if (error) {
    console.error('[platformService] createAutomation error:', error.message);
    return null;
  }
  return data as Automation;
}

export async function updateAutomation(
  id: string,
  updates: Partial<Pick<Automation, 'name' | 'description' | 'trigger_type' | 'trigger_config' | 'action_type' | 'action_config' | 'conditions' | 'enabled'>>
): Promise<boolean> {
  const { error } = await supabase
    .from('automations')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);

  return !error;
}

export async function deleteAutomation(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('automations')
    .delete()
    .eq('id', id);

  return !error;
}

export async function toggleAutomation(id: string, enabled: boolean): Promise<boolean> {
  return updateAutomation(id, { enabled });
}

export async function getAutomationRuns(
  automationId: string,
  limit = 20
): Promise<AutomationRun[]> {
  const { data, error } = await supabase
    .from('automation_runs')
    .select('*')
    .eq('automation_id', automationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) return [];
  return (data as AutomationRun[]) || [];
}

// ─── App Logs ─────────────────────────────────────────────────

export async function getAppLogs(
  workspaceId: string,
  appId?: string,
  limit = 100
): Promise<AppLog[]> {
  let query = supabase
    .from('app_logs')
    .select('*')
    .eq('workspace_id', workspaceId);

  if (appId) {
    query = query.eq('app_id', appId);
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) return [];
  return (data as AppLog[]) || [];
}

// ─── App Usage Metrics ────────────────────────────────────────

export async function getAppUsageMetrics(
  workspaceId: string,
  appId?: string
): Promise<AppUsageMetric[]> {
  let query = supabase
    .from('app_usage_metrics')
    .select('*')
    .eq('workspace_id', workspaceId);

  if (appId) {
    query = query.eq('app_id', appId);
  }

  const { data, error } = await query.order('period_start', { ascending: false }).limit(30);
  if (error) return [];
  return (data as AppUsageMetric[]) || [];
}

// ─── App Reviews ──────────────────────────────────────────────

export async function getAppReviews(appId: string): Promise<AppReview[]> {
  const { data, error } = await supabase
    .from('app_reviews')
    .select('*')
    .eq('app_id', appId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) return [];
  return (data as AppReview[]) || [];
}

export async function submitAppReview(review: {
  app_id: string;
  workspace_id: string;
  rating: number;
  review?: string;
}): Promise<AppReview | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('app_reviews')
    .upsert(
      { ...review, user_id: user.id },
      { onConflict: 'app_id,user_id' }
    )
    .select()
    .single();

  if (error) return null;
  return data as AppReview;
}

// ─── App Versions ─────────────────────────────────────────────

export async function getAppVersions(appId: string): Promise<AppVersion[]> {
  const { data, error } = await supabase
    .from('app_versions')
    .select('*')
    .eq('app_id', appId)
    .order('created_at', { ascending: false });

  if (error) return [];
  return (data as AppVersion[]) || [];
}

// ─── Dashboard Widgets ────────────────────────────────────────

export async function getWorkspaceWidgets(workspaceId: string): Promise<DashboardWidget[]> {
  const { data, error } = await supabase
    .from('dashboard_widgets')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('enabled', true)
    .order('position', { ascending: true });

  if (error) return [];
  return (data as DashboardWidget[]) || [];
}

export async function createWidget(widget: {
  workspace_id: string;
  app_id?: string;
  widget_type: string;
  title: string;
  config?: Record<string, unknown>;
  size?: string;
}): Promise<DashboardWidget | null> {
  const { data, error } = await supabase
    .from('dashboard_widgets')
    .insert(widget)
    .select()
    .single();

  if (error) return null;
  return data as DashboardWidget;
}

// ─── Developer Accounts ──────────────────────────────────────

export async function getDeveloperAccount(): Promise<DeveloperAccount | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('developer_accounts')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error) return null;
  return data as DeveloperAccount;
}

export async function createDeveloperAccount(account: {
  company_name?: string;
  website?: string;
  bio?: string;
}): Promise<DeveloperAccount | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('developer_accounts')
    .insert({ ...account, user_id: user.id })
    .select()
    .single();

  if (error) return null;
  return data as DeveloperAccount;
}

export async function getDeveloperApps(): Promise<MarketplaceApp[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('apps')
    .select('*')
    .eq('developer_id', user.id)
    .order('created_at', { ascending: false });

  if (error) return [];
  return (data as MarketplaceApp[]) || [];
}

export async function createApp(app: {
  name: string;
  slug: string;
  description: string;
  category: string;
  pricing_type?: string;
  tags?: string[];
}): Promise<MarketplaceApp | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('apps')
    .insert({
      ...app,
      developer_id: user.id,
      developer_name: user.email?.split('@')[0] || 'Developer',
      status: 'draft',
    })
    .select()
    .single();

  if (error) {
    console.error('[platformService] createApp error:', error.message);
    return null;
  }
  return data as MarketplaceApp;
}

export async function updateApp(
  appId: string,
  updates: Partial<Pick<MarketplaceApp, 'name' | 'description' | 'short_description' | 'category' | 'icon_url' | 'tags' | 'pricing_type'>>
): Promise<boolean> {
  const { error } = await supabase
    .from('apps')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', appId);

  return !error;
}

export async function submitAppForReview(appId: string): Promise<boolean> {
  const { error } = await supabase
    .from('apps')
    .update({ status: 'review', updated_at: new Date().toISOString() })
    .eq('id', appId);

  return !error;
}

// ─── Admin App Management ─────────────────────────────────────

export async function adminGetAllApps(): Promise<MarketplaceApp[]> {
  const { data, error } = await supabase
    .from('apps')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[platformService] adminGetAllApps error:', error.message);
    return [];
  }
  return (data as MarketplaceApp[]) || [];
}

export async function adminSetAppStatus(appId: string, status: 'published' | 'suspended' | 'draft' | 'review'): Promise<boolean> {
  const { error } = await supabase
    .from('apps')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', appId);

  return !error;
}

export async function adminCreateOfficialApp(app: {
  name: string;
  slug: string;
  description: string;
  short_description?: string;
  category: string;
  pricing_type: string;
  tags: string[];
  developer_name?: string;
}): Promise<MarketplaceApp | null> {
  const { data, error } = await supabase
    .from('apps')
    .insert({
      ...app,
      developer_name: app.developer_name ?? 'Workflow Official',
      status: 'published',
      visibility: 'public',
    })
    .select()
    .single();

  if (error) {
    console.error('[platformService] adminCreateOfficialApp error:', error.message);
    return null;
  }
  return data as MarketplaceApp;
}
